import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { DiscountCodesService } from './services/discount-codes.service';
import { GroupService } from './services/group.service';
import { UserService } from './services/user.service';

import { CapitalizePipe } from './pipes/capitalize.pipe';
import { LoginHeadersFilterPipe } from './pipes/login-headers.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { XsactDatePipe } from './pipes/xsact-datePipe';
import { XsactTokensToUSDPipe } from './pipes/xsact-tokensToUSDPipe';

// Shared elements
import { ElementsSpinnerComponent } from './elements/elements-spinner/elements-spinner.component';
import { ElementsTransactionListComponent } from './elements/elements-transaction-list/elements-transaction-list.component';
import { ElementsTransactionSingleComponent } from './elements/elements-transaction-single/elements-transaction-single.component';

import { ActivationCodeComponent } from './discount-codes/code-activation/code-activation.component';
import { DiscountCodesComponent } from './discount-codes/discount-codes.component';
import { ElementsAuditLogComponent } from './elements/elements-audit-log/elements-audit-log.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { NotificationBarComponent } from './notification-bar/notification-bar.component';
import { PublisherLogoComponent } from './publisher-banner-image/publisher-banner-image.component';

import { XsactBodyComponent } from './xsact-body/index';
import { XsactFooterComponent } from './xsact-footer/index';
import { XsactHeaderComponent } from './xsact-header/index';
import { XsactLeftNavComponent, XsactLeftNavLinkComponent } from './xsact-left-nav/index';

import { ContactFormComponent } from './contact-form/index';
import { PhoneNumberComponent } from './phone-number/index';

import { ImageService } from './services/image.service';
import { OAuth2Service } from './services/oauth2.service';

import { CountryService } from './services/country';
import { PhoneCodes } from './services/country-phone-codes';
import { StateService } from './services/states';
import { UtilityService } from './services/utility';

import {
  FormElementsButtonEditComponent,
  FormElementsChangePasswordComponent,
  FormElementsDocumentComponent,
  FormElementsFieldErrorMessageComponent,
  FormElementsSavingMessageComponent,
  FormElementsUserNameAndEmailComponent,
  LoadingButtonComponent,
  TwoFactorAuthSettingsComponent,
  ValidateEqualDirective,
  ValidatedInputComponent,
  ValidationMessageComponent,
} from './form-elements/index';

export const TRANSACT_FORM_ELEMENTS: any[] = [
  FormElementsButtonEditComponent,
  FormElementsChangePasswordComponent,
  FormElementsDocumentComponent,
  FormElementsFieldErrorMessageComponent,
  FormElementsSavingMessageComponent,
  FormElementsUserNameAndEmailComponent,
  TwoFactorAuthSettingsComponent,
  LoadingButtonComponent,
  ValidateEqualDirective,
  ValidatedInputComponent,
  ValidationMessageComponent,
];

import { DirectivesModule } from '@shared/directives/directives.module';

// karl: The other way to handle ngx-bootstrap  just import what we use.
import { AlertModule } from 'ngx-bootstrap/alert';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { CurrencySelectorComponent } from './currency-selector/currency-selector.component';
import { RecaptchaComponent } from './recaptcha/recaptcha.component';
import { AuditService } from './services/audit.service';
import { ToastModule } from './toast/toast.module';

/**
 * Do not specify providers for modules that might be imported by a lazy loaded module.
 */
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DirectivesModule,

    AlertModule.forRoot(),
    BsDropdownModule.forRoot(),
    ModalModule.forRoot(),
    ToastModule,
  ],
  declarations: [
    TRANSACT_FORM_ELEMENTS,

    CapitalizePipe,
    TruncatePipe,
    XsactDatePipe,
    LoginHeadersFilterPipe,
    XsactTokensToUSDPipe,
    ElementsSpinnerComponent,
    ElementsTransactionListComponent,
    ElementsTransactionSingleComponent,
    ElementsAuditLogComponent,
    XsactBodyComponent,
    XsactFooterComponent,
    XsactHeaderComponent,
    XsactLeftNavComponent,
    XsactLeftNavLinkComponent,
    ContactFormComponent,
    PhoneNumberComponent,
    NotificationBarComponent,
    FileUploadComponent,
    DiscountCodesComponent,
    ActivationCodeComponent,
    PublisherLogoComponent,
    CurrencySelectorComponent,
    RecaptchaComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ToastModule,

    TRANSACT_FORM_ELEMENTS,

    CapitalizePipe,
    TruncatePipe,
    XsactDatePipe,
    XsactTokensToUSDPipe,
    LoginHeadersFilterPipe,

    ElementsSpinnerComponent,
    ElementsTransactionListComponent,
    ElementsTransactionSingleComponent,
    ElementsAuditLogComponent,
    NotificationBarComponent,
    FileUploadComponent,
    DiscountCodesComponent,
    ActivationCodeComponent,

    XsactBodyComponent,
    XsactFooterComponent,
    XsactHeaderComponent,
    XsactLeftNavComponent,
    XsactLeftNavLinkComponent,
    ContactFormComponent,
    PhoneNumberComponent,
    DirectivesModule,
    PublisherLogoComponent,
    CurrencySelectorComponent,
    RecaptchaComponent,
  ],
  providers: [OAuth2Service, StateService, CountryService, PhoneCodes],
})
export class XsactSharedModule {
  static forRoot(): ModuleWithProviders<XsactSharedModule> {
    return {
      ngModule: XsactSharedModule,
      providers: [
        UserService,
        GroupService,
        DiscountCodesService,
        CapitalizePipe,
        ImageService,
        TruncatePipe,
        XsactDatePipe,
        XsactTokensToUSDPipe,
        LoginHeadersFilterPipe,
        UtilityService,
        AuditService,
      ],
    };
  }
}
